import React, { useEffect, useMemo, useState } from "react";
import { X, Search } from "lucide-react";
import { useChains, isSignableSource, chainTypeLabel } from "@/lib/chains";

export default function ChainModal({ open, onClose, onSelect, side, excludeSolana }) {
  const chainsAll = useChains();
  const [q, setQ] = useState("");
  useEffect(() => { if (open) setQ(""); }, [open]);

  const chains = useMemo(() => {
    if (!chainsAll) return null;
    let list = chainsAll;
    if (side === "source") list = list.filter(isSignableSource);
    if (excludeSolana) list = list.filter((c) => !c.isSolana);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((c) =>
        c.name.toLowerCase().includes(s) ||
        c.symbol?.toLowerCase().includes(s) ||
        String(c.id).includes(s)
      );
    }
    return list;
  }, [chainsAll, side, excludeSolana, q]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-[rgba(15,23,42,0.35)] backdrop-blur-md" />
      <div className="relative panel w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 h-12 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="text-[13px] text-[var(--text)] font-semibold">Select {side} network</div>
            <div className="text-[11px] text-[var(--text-muted)] font-mono">
              {chains ? `${chains.length} networks` : "Loading…"}
            </div>
          </div>
          <button
            className="w-8 h-8 rounded-md text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--panel-2)] flex items-center justify-center"
            onClick={onClose}
            data-testid="chain-modal-close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="px-3 py-2 border-b border-[var(--border)] flex items-center gap-2">
          <Search size={13} className="text-[var(--text-muted)] flex-shrink-0" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search 70+ networks…"
            className="bg-transparent outline-none text-[13px] text-[var(--text)] placeholder:text-[var(--text-dim)] flex-1"
            data-testid="chain-search-input"
          />
        </div>

        <div className="p-2 grid grid-cols-1 sm:grid-cols-2 max-h-[70vh] overflow-y-auto">
          {!chains && (
            <div className="col-span-full text-[12px] text-[var(--text-muted)] p-6 text-center font-mono">
              Fetching networks…
            </div>
          )}
          {chains && chains.length === 0 && (
            <div className="col-span-full text-[12px] text-[var(--text-muted)] p-6 text-center font-mono">
              No networks match "{q}"
            </div>
          )}
          {chains && chains.map((c) => (
            <button
              key={`${c.chainType}-${c.id}`}
              onClick={() => { onSelect(c); onClose(); }}
              className="flex items-center gap-3 p-3 rounded-md hover:bg-[var(--panel-2)] transition-colors text-left"
              data-testid={`chain-option-${c.key}`}
            >
              <img src={c.icon} alt={c.name} className="w-7 h-7 rounded-full flex-shrink-0 bg-[var(--panel-2)]" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
              <div className="min-w-0">
                <div className="text-[13px] text-[var(--text)] font-semibold truncate">{c.name}</div>
                <div className="text-[10.5px] text-[var(--text-subtle)] font-mono uppercase tracking-wider">
                  {chainTypeLabel(c)} · {c.symbol || "—"}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
